import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ScopeActivationItem } from "@/api/scope-activation";

export interface EvidenceFlow {
  signal: string;
  target: string;
  capability: string;
  decisionRuleText: string;
}

type EvidenceSource = Pick<
  ScopeActivationItem,
  "capability_details" | "triggered_by" | "s4_targets" | "refused_tables"
>;

/** Deriva as linhas sinal ECC -> alvo S/4 (+ justificativa da regra) por capacidade. */
export function evidenceFlows(item: EvidenceSource): EvidenceFlow[] {
  const caps = item.capability_details ?? [];
  if (caps.length > 0) {
    return caps.map((c) => ({
      signal: c.triggered_by.join(", ") || "—",
      target: c.s4_target || "—",
      capability: c.capability,
      decisionRuleText: c.decision_rule_text || "",
    }));
  }
  if (item.triggered_by && item.triggered_by.length > 0) {
    return [
      {
        signal: item.triggered_by.join(", ") || "—",
        target: (item.s4_targets ?? []).join(", ") || "—",
        capability: "",
        decisionRuleText: "",
      },
    ];
  }
  return [];
}

/**
 * Bloco "por quê": sinal ECC -> alvo S/4 por capacidade, com a justificativa da
 * regra quando disponível. Compartilhado entre WorklistView e o drilldown do
 * Catalog.
 */
export function CapabilityEvidence({
  item,
  emptyHint,
}: {
  item: EvidenceSource;
  emptyHint?: string;
}) {
  const { t } = useTranslation();
  const flows = evidenceFlows(item);
  const refused = item.refused_tables ?? [];

  // Sem sinal porque o SAP não liberou a leitura: dizer isso, e não "nenhum sinal".
  if (flows.length === 0 && refused.length > 0) {
    return (
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[10px] uppercase tracking-wide text-amber-700">
          {t("Table read not released in SAP")}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(
            "SAP did not release reading {{tables}}. Without it there is no way to tell whether the client uses this item.",
            { tables: refused.join(", ") },
          )}
        </span>
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          {t("Scope decision pending")}
        </span>
        <span className="text-xs text-muted-foreground">
          {emptyHint ?? t("No direct signal from ERP — depends on the client's scope decision.")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
        {t("Why it needs action")}
      </span>
      {flows.map((f, idx) => (
        <div key={idx} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[11px]">
              {f.signal}
            </span>
            <ArrowRight className="h-3 w-3 text-amber-600" />
            <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[11px]">
              {f.target}
            </span>
            {f.capability && (
              <span className="text-[11px] text-muted-foreground">
                {f.capability}
              </span>
            )}
          </div>
          {f.decisionRuleText && (
            <span className="pl-1 text-[11px] italic text-muted-foreground">
              {f.decisionRuleText}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
